import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormToggle from '../../../components/form/FormToggle';

import {
  whatsappTemplatesAdd,
  whatsappTemplatesUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormTextarea from '../../../components/form/FormTextarea';

const AddEditTemplateModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const initialValues = {
    template_name: '',
    template_content: '',
    status: 'true',
    file: '',
    type: 'text'
  };

  const validationSchema = Yup.object({
    template_name: Yup.string().required('template name is required'),
    template_content: Yup.string().required('template content is required')
  });

  const onSubmit = async () => {
    const buildFormData = () => {
      const formData = new FormData();

      Object.entries(values).forEach(([key, value]) => {
        if (key !== 'file') {
          formData.append(key, value);
        }
      });

      if (values?.file) {
        formData.append('file', values.file);
      }

      return formData;
    };

    try {
      const formData = buildFormData();

      let response;
      if (isEdit) {
        response = await dispatch(
          whatsappTemplatesUpdate({ formData: formData, id: editRecord.id })
        ).unwrap();
      } else {
        response = await dispatch(whatsappTemplatesAdd(formData)).unwrap();
      }

      if (response && response?.message) {
        toast.success(`Record ${isEdit ? 'Updated' : 'Added'} Successfully`);
        onCloseClick(true);
        setFileName('');
        resetForm();
      } else {
        toast.error(response?.error || 'Something went wrong');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (isEdit) {
      setValues({
        ...values,
        template_name: editRecord?.template_name,
        template_content: editRecord?.template_content,
        status: String(editRecord?.status),
        file: editRecord?.template_url || '',
        type: editRecord?.type || ''
      });
      setFileName(editRecord?.filename || '');
    }
  }, [editRecord]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
      setFileName(file.name);
      setFieldValue('file', file);
    } else {
      toast.error('Please upload a PDF or image file.');
      fileInputRef.current.value = '';
      setFileName('');
    }
  };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Whatsapp Template' : 'Add Whatsapp Template'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="whatsapp-templates" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="template_name"
                label="Name"
                placeholder="Enter Template Name"
                value={values.template_name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="type"
                label="Type"
                trueName="Text"
                falseName="Media"
                value={String(values.type === 'text')}
                onChange={() => {
                  setFieldValue('type', values.type === 'text' ? 'media' : 'text');
                  if (values.type === 'text') {
                    setFieldValue('file', '');
                    setFileName('');
                  }
                }}
              />
            </Col>
            <Col lg="6">
              <FormTextarea
                name="template_content"
                label="Template"
                placeholder="Enter template"
                value={values.template_content}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                rows={1}
              />
            </Col>
            {values.type === 'media' ? (
              <Col lg="3">
                <Label>Template Media</Label>
                <div className="file-upload-container">
                  <Input
                    type="text"
                    className="file-name"
                    placeholder={fileName ? fileName : 'Choose file'}
                    readOnly
                  />
                  <label className="upload-button">
                    <Icon.Upload size={18} />
                    <Input
                      type="file"
                      id="file-upload"
                      className="file-upload"
                      onChange={handleFileChange}
                      accept="application/pdf,image/*"
                      ref={fileInputRef}
                    />
                  </label>
                </div>
              </Col>
            ) : null}
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setFileName('');
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="whatsapp-templates" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditTemplateModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditTemplateModal;
